import { useTranslation } from '@pypestream/translations';
import {
  type GroupSpecOptions,
  type LlmCatalogResult,
  llmModelChoices,
} from '@pypestream/ui-patterns';
import { useEffect, useState } from 'react';

import {
  fetchOrgAiModels,
  fetchOrgAiProviders,
  type MaestroAiProvider,
  type MaestroError,
  reasonFor,
} from './maestro-org-api';

type Fetched = LlmCatalogResult & { reason?: MaestroError };

/**
 * The org Model card's Provider select and model picker. Listing models needs the manage
 * permission, so `enabled` is false for a viewer, who keeps the plain read-only fields. Only the
 * chosen `provider`'s models are fetched, each provider once.
 */
export function useOrgLlmOptions(
  orgId: string,
  enabled: boolean,
  provider: string | undefined
): {
  options: Pick<GroupSpecOptions, 'models' | 'providers'>;
  error: string | null;
} {
  const { t } = useTranslation();
  const [providers, setProviders] = useState<
    MaestroAiProvider[] | MaestroError | null
  >(null);
  const [catalogs, setCatalogs] = useState<Record<string, Fetched>>({});

  useEffect(() => {
    if (!enabled || !orgId) return;
    let current = true;
    setProviders(null);
    setCatalogs({});
    fetchOrgAiProviders(orgId).then(
      (list) => current && setProviders(list),
      (error) => current && setProviders(reasonFor(error).reason)
    );
    return () => {
      current = false;
    };
  }, [orgId, enabled]);

  const offered = Array.isArray(providers)
    ? providers.find(({ id }) => id === provider)
    : undefined;
  const fetched = offered ? catalogs[offered.id] : undefined;

  useEffect(() => {
    if (!enabled || !offered || fetched) return;
    let current = true;
    const base = { provider: offered.id, label: offered.label };
    fetchOrgAiModels(orgId, offered.id)
      .then(
        (result): Fetched =>
          result.ok
            ? { ...base, models: result.models }
            : { ...base, models: [], error: result.error },
        (error): Fetched => ({
          ...base,
          models: [],
          reason: reasonFor(error).reason,
        })
      )
      .then(
        (catalog) =>
          current &&
          setCatalogs((all) => ({ ...all, [catalog.provider]: catalog }))
      );
    return () => {
      current = false;
    };
  }, [orgId, enabled, offered, fetched]);

  if (!enabled || providers === null) return { options: {}, error: null };
  const errorKey = (reason: MaestroError) =>
    String(t(`manager/organizations:orgTabs.errors.${reason}`));
  if (!Array.isArray(providers))
    return { options: {}, error: errorKey(providers) };

  const models = llmModelChoices(Object.values(catalogs));
  const key = (name: string) => `manager/organizations:orgTabs.models.${name}`;
  const failure = !fetched
    ? null
    : fetched.reason
      ? errorKey(fetched.reason)
      : fetched.models.length === 0
        ? String(
            t(key('failed'), {
              details: `${fetched.label}: ${fetched.error || t(key('empty'))}`,
            })
          )
        : null;
  return {
    options: {
      models: models.length > 0 ? models : undefined,
      providers: providers.map(({ id, label }) => ({ value: id, label })),
    },
    error: failure,
  };
}
